import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, Search, Plus, Store, Calendar, CheckCircle, Box, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { staffSelfAPI, itemsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const RecordPlacementPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [placements, setPlacements] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    retailer_id: '',
    product_id: '',
    quantity: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [placementsRes, retailersRes, productsRes] = await Promise.all([
        staffSelfAPI.getMyPlacements(),
        staffSelfAPI.getMyRetailersForForm(),
        itemsAPI.list({ limit: 100 }),
      ]);
      setPlacements(placementsRes.data.placements || []);
      setRetailers(retailersRes.data.retailers || []);
      setProducts(productsRes.data.items || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load placements'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.retailer_id || !formData.product_id || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await staffSelfAPI.recordPlacement({
        retailer_id: formData.retailer_id,
        product_id: formData.product_id,
        quantity: parseInt(formData.quantity),
        notes: formData.notes,
      });
      toast.success('Placement recorded successfully!');
      setShowForm(false);
      setFormData({ retailer_id: '', product_id: '', quantity: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to record placement'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredPlacements = placements.filter(p => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        p.party_name?.toLowerCase().includes(search) ||
        p.item_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats = {
    total: placements.length,
    todayCount: placements.filter(p => {
      const today = new Date().toDateString();
      return new Date(p.created_at).toDateString() === today;
    }).length,
    totalUnits: placements.reduce((sum, p) => sum + (p.quantity || 0), 0),
    uniqueRetailers: [...new Set(placements.map(p => p.party_id))].length,
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Fixed Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Product Placements</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Record and track product placements at retailers</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Record Placement
            </button>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <Package className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.total}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-teal-50 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-teal-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Today</p>
                <p className="text-[14px] font-semibold text-teal-600">{stats.todayCount}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <Box className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Units</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.totalUnits}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center">
                <Store className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Retailers</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.uniqueRetailers}</p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by retailer or product..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-md pl-9 pr-4 py-2 bg-white rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition-all placeholder:text-slate-400 shadow-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Table Area */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col">
          <div className="tl-table-container flex-1 flex flex-col overflow-hidden">
            
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading placements...</p>
                </div>
              </div>
            ) : filteredPlacements.length === 0 ? (
              <div className="tl-empty-state flex-1 flex flex-col items-center justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <Package className="w-7 h-7 text-slate-400" />
                </div>
                <p className="tl-empty-title">
                  {searchQuery ? 'No placements found' : 'No placements yet'}
                </p>
                <p className="tl-empty-description mt-1 mb-4">
                  {searchQuery ? 'Try adjusting your search terms' : 'Start recording product placements'}
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Record First Placement
                </button>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th tl-th-teal" style={{ width: '25%' }}>Retailer</th>
                        <th className="tl-th tl-th-teal" style={{ width: '25%' }}>Product</th>
                        <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>Qty</th>
                        <th className="tl-th tl-th-teal" style={{ width: '20%' }}>Date</th>
                        <th className="tl-th tl-th-teal" style={{ width: '20%' }}>Notes</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody>
                      {filteredPlacements.map((placement, index) => (
                        <tr key={placement.id} className={`tl-tr ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}>
                          <td className="tl-td" style={{ width: '25%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {placement.party_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <span className="tl-cell-primary truncate">{placement.party_name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '25%' }}>
                            {placement.item_name || '—'}
                          </td>
                          <td className="tl-td tl-td-right" style={{ width: '10%' }}>
                            <span className="font-medium text-slate-700">{placement.quantity}</span>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '20%' }}>
                            {formatDateTime(placement.created_at)}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '20%' }}>
                            {placement.notes || <span className="tl-cell-empty">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div className="tl-table-footer flex items-center justify-between">
                  <p>Showing <span className="tl-table-footer-emphasis">{filteredPlacements.length}</span> placements</p>
                  <p>Total Units: <span className="tl-table-footer-emphasis">{stats.totalUnits}</span></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Record Placement Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-[17px] font-semibold text-slate-800">Record Placement</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-[12px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Retailer *</label>
                <select
                  value={formData.retailer_id}
                  onChange={(e) => setFormData({...formData, retailer_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                  required
                >
                  <option value="">Select retailer</option>
                  {retailers.map(r => (
                    <option key={r.id || r.party_id} value={r.id || r.party_id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Product *</label>
                <select
                  value={formData.product_id}
                  onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                  required
                >
                  <option value="">Select product</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Quantity *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                  placeholder="Enter quantity"
                  required
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none resize-none"
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 text-[13px] font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Placement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordPlacementPage;
